import { pdf } from "@react-pdf/renderer";
import { IBucket } from "../../../views/Orders/store/useBucketsStore";
import { PartQuantity, SelectedSupplier } from "./partsCalculationUtils";
import BucketAvailabilityPdf from "../BucketAvailabilityPdf";

export interface BucketAvailabilityRow {
  partName: string;
  mrp?: number;
  orderedQty: number;
  availableQty: number;
  remainingQty: number;
}

/**
 * Reads MRP from an order item / part-like object. APIs may use `mrp`, `MRP`,
 * or other keys.
 */
export function pickMrpFromOrderItemLike(candidate: unknown): number | undefined {
  if (candidate == null || typeof candidate !== "object") return undefined;
  const o = candidate as Record<string, unknown>;
  const nestedPart = o.part;
  const raw =
    o.mrp ??
    o.MRP ??
    o.maxRetailPrice ??
    o.max_retail_price ??
    (nestedPart != null && typeof nestedPart === "object"
      ? (nestedPart as Record<string, unknown>).mrp ??
        (nestedPart as Record<string, unknown>).MRP
      : undefined);
  if (raw === null || raw === undefined || raw === "") return undefined;
  const n = Number(raw);
  if (Number.isNaN(n)) return undefined;
  return n;
}

function hasUsableMrp(mrp: PartQuantity["mrp"]): boolean {
  if (mrp === undefined) return false;
  const n = Number(mrp);
  return !Number.isNaN(n);
}

/**
 * Fills missing `mrp` on parts from matching `orderItems` (by partId, else rootPartNumber).
 */
export function enrichPartQuantitiesWithOrderMrp(
  parts: PartQuantity[],
  orderItems: unknown[] | undefined | null
): PartQuantity[] {
  if (!orderItems?.length) return parts;
  return parts.map((part) => {
    if (hasUsableMrp(part.mrp)) return part;
    const match =
      orderItems.find(
        (oi) =>
          oi != null &&
          typeof oi === "object" &&
          (part.partId
            ? (oi as { partId?: string }).partId === part.partId
            : (oi as { rootPartNumber?: string }).rootPartNumber ===
              part.rootPartNumber)
      ) ?? null;
    const mrp = pickMrpFromOrderItemLike(match);
    if (mrp === undefined) return part;
    return { ...part, mrp };
  });
}

export function buildBucketAvailabilityRows(bucket: IBucket): {
  availableRows: BucketAvailabilityRow[];
  delayedRows: BucketAvailabilityRow[];
} {
  const originalQuantities = new Map<string, number>();
  bucket.originalParts.forEach((part) => {
    originalQuantities.set(part.rootPartNumber, part.quantity);
  });

  const fulfilledQuantities = new Map<string, number>();
  bucket.suppliers.forEach((supplier: SelectedSupplier) => {
    supplier.selectedParts.forEach((part) => {
      const currentFulfilled =
        fulfilledQuantities.get(part.rootPartNumber) || 0;
      const originalQuantity = originalQuantities.get(part.rootPartNumber) || 0;
      const newFulfilled = Math.min(
        currentFulfilled + part.familyQuantity,
        originalQuantity
      );
      fulfilledQuantities.set(part.rootPartNumber, newFulfilled);
    });
  });

  const availableRows: BucketAvailabilityRow[] = [];
  const delayedRows: BucketAvailabilityRow[] = [];

  bucket.originalParts.forEach((part) => {
    const fulfilledQty = fulfilledQuantities.get(part.rootPartNumber) || 0;
    const remainingQty = Math.max(0, part.quantity - fulfilledQty);
    const partName = part.partName || "Part Name Not Available";

    if (fulfilledQty > 0) {
      availableRows.push({
        partName,
        mrp: part.mrp,
        orderedQty: part.quantity,
        availableQty: fulfilledQty,
        remainingQty: 0,
      });
    }
    if (remainingQty > 0) {
      delayedRows.push({
        partName,
        mrp: part.mrp,
        orderedQty: part.quantity,
        availableQty: 0,
        remainingQty,
      });
    }
  });

  return { availableRows, delayedRows };
}

export interface DownloadBucketAvailabilityPdfHeader {
  customerName: string;
  /** e.g. en-IN short date */
  documentDate: string;
}

export async function downloadBucketAvailabilityPdf(
  bucket: IBucket,
  orderNumber: string,
  header: DownloadBucketAvailabilityPdfHeader
): Promise<void> {
  const { availableRows, delayedRows } = buildBucketAvailabilityRows(bucket);
  const safeOrder = (orderNumber || "order").replace(/[^\w.-]+/g, "_");
  const idSlice = bucket.id.slice(0, 8);

  const logoSrc =
    typeof window !== "undefined"
      ? new URL(
          `${process.env.PUBLIC_URL || ""}/images/garaaz-logo.png`,
          window.location.origin
        ).href
      : undefined;

  try {const blob = await pdf(
    <BucketAvailabilityPdf
      availableRows={availableRows}
      delayedRows={delayedRows}
      orderNumber={orderNumber || "—"}
      customerName={header.customerName || "—"}
      documentDate={header.documentDate}
      logoSrc={logoSrc}
    />
  ).toBlob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `bucket-parts-${safeOrder}-${idSlice}.pdf`;
  a.click();
  URL.revokeObjectURL(url);
} catch (error) {
  console.error("Failed to generate PDF:", error);
  throw error;
}
}
